import { z } from 'zod';

export const SupplierConnectionState = Object.freeze({
  Pending: 'pending',
  Accepted: 'accepted',
  Declined: 'declined',
  Expired: 'expired',
  Cancelled: 'cancelled',
  Terminated: 'terminated',
});

export const SupplierDataRequestState = Object.freeze({
  Pending: 'pending',
  Accepted: 'accepted',
  InProgress: 'in_progress',
  PartiallyFulfilled: 'partially_fulfilled',
  Fulfilled: 'fulfilled',
  Declined: 'declined',
  Cancelled: 'cancelled',
});

export const SupplierDataRequestLineFulfilmentState = Object.freeze({
  Unfulfilled: 'unfulfilled',
  Fulfilled: 'fulfilled',
});

export const SupplierInboxNextAction = Object.freeze({
  ReviewConnection: 'review_connection',
  ReviewRequest: 'review_request',
  FulfilRequest: 'fulfil_request',
  ViewRequest: 'view_request',
});

export const SupplierConnectionOrigin = Object.freeze({
  Invitation: 'invitation',
  LegacyBackfill: 'legacy_backfill',
});

const objectIdV1Schema = z.string().regex(/^[a-f\d]{24}$/i);

// Mirrors the backend's `supplierConnectionProvenanceFields` + provenance refinement
// (backend `src/contracts/supplierConnection.ts`). A connection is either invitation-born
// — carrying `createdFromInviteId` and no explicit `origin` — or legacy-backfilled, which
// the backend serialises as a bare `{ origin: 'legacy_backfill' }` with NO invite id.
const supplierConnectionProvenanceFields = {
  createdFromInviteId: objectIdV1Schema.optional(),
  origin: z.nativeEnum(SupplierConnectionOrigin).optional(),
};

const validateSupplierConnectionProvenance = (connection, context) => {
  const origin = connection.origin ?? SupplierConnectionOrigin.Invitation;
  const valid =
    (origin === SupplierConnectionOrigin.Invitation && Boolean(connection.createdFromInviteId)) ||
    (origin === SupplierConnectionOrigin.LegacyBackfill && !connection.createdFromInviteId);
  if (!valid) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid supplier connection provenance',
    });
  }
};

export const supplierDataRequestLineV1Schema = z
  .object({
    cnCode: z.string().min(1),
    interval: z
      .object({
        startInclusive: z.string().datetime(),
        endExclusive: z.string().datetime(),
      })
      .strict(),
    installation: z.string().optional(),
    product: z.string().optional(),
    method: z.string().optional(),
    fulfilment: z
      .object({
        state: z.nativeEnum(SupplierDataRequestLineFulfilmentState),
        coveringRevisionIds: z.array(objectIdV1Schema),
      })
      .strict(),
  })
  .strict();

export const supplierConnectionInboxItemV1Schema = z
  .object({
    version: z.literal(1),
    id: objectIdV1Schema,
    declarantId: objectIdV1Schema,
    state: z.nativeEnum(SupplierConnectionState),
    epoch: z.number().int().positive(),
    ...supplierConnectionProvenanceFields,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()
  .superRefine(validateSupplierConnectionProvenance);

export const supplierDataRequestInboxItemV1Schema = z
  .object({
    version: z.literal(1),
    id: objectIdV1Schema,
    connectionId: objectIdV1Schema,
    declarantId: objectIdV1Schema,
    state: z.nativeEnum(SupplierDataRequestState),
    reportingPeriod: z.string().min(1),
    lines: z.array(supplierDataRequestLineV1Schema).min(1),
    createdFromInviteId: objectIdV1Schema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();

export const supplierConnectionCommandResponseV1Schema = z.discriminatedUnion('status', [
  z
    .object({
      version: z.literal(1),
      status: z.literal(SupplierConnectionState.Accepted),
      connection: supplierConnectionInboxItemV1Schema,
    })
    .strict(),
  z
    .object({
      version: z.literal(1),
      status: z.literal(SupplierConnectionState.Declined),
      connection: supplierConnectionInboxItemV1Schema,
    })
    .strict(),
  z
    .object({
      version: z.literal(1),
      status: z.literal(SupplierConnectionState.Terminated),
      connection: supplierConnectionInboxItemV1Schema,
    })
    .strict(),
]);

export const supplierDataRequestCommandResponseV1Schema = z.discriminatedUnion('status', [
  z
    .object({
      version: z.literal(1),
      status: z.literal(SupplierDataRequestState.Accepted),
      request: supplierDataRequestInboxItemV1Schema,
    })
    .strict(),
  z
    .object({
      version: z.literal(1),
      status: z.literal(SupplierDataRequestState.Declined),
      request: supplierDataRequestInboxItemV1Schema,
    })
    .strict(),
  z
    .object({
      version: z.literal(1),
      status: z.literal(SupplierDataRequestState.Cancelled),
      request: supplierDataRequestInboxItemV1Schema,
    })
    .strict(),
]);

export const supplierConnectionAndRequestCommandResponseV1Schema = z
  .object({
    version: z.literal(1),
    status: z.literal('accepted'),
    connection: supplierConnectionInboxItemV1Schema,
    request: supplierDataRequestInboxItemV1Schema,
  })
  .strict();

export const supplierInboxNextActionV1Schema = z.nativeEnum(SupplierInboxNextAction).nullable();

export const supplierInboxRequestV1Schema = supplierDataRequestInboxItemV1Schema
  .extend({ nextAction: supplierInboxNextActionV1Schema })
  .strict();

export const supplierInboxConnectionV1Schema = z
  .object({
    version: z.literal(1),
    id: objectIdV1Schema,
    declarant: z
      .object({
        id: objectIdV1Schema,
        name: z.string().min(1),
      })
      .strict(),
    state: z.nativeEnum(SupplierConnectionState),
    epoch: z.number().int().positive(),
    ...supplierConnectionProvenanceFields,
    requests: z.array(supplierInboxRequestV1Schema),
    nextAction: supplierInboxNextActionV1Schema,
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict()
  .superRefine(validateSupplierConnectionProvenance);

export const supplierInboxResponseV1Schema = z
  .object({
    version: z.literal(1),
    items: z.array(supplierInboxConnectionV1Schema),
  })
  .strict();
